/**
 * Error Handling Utilities
 *
 * Provides type-safe error message extraction for API errors
 */

/**
 * Safely extracts an error message from an unknown error object
 * Handles API errors with response.data.detail, Error objects with message, and unknown errors
 */
export function getErrorMessage(error: unknown): string {
  // Handle null/undefined
  if (!error) {
    return 'An unknown error occurred';
  }

  // Check if it's an object
  if (typeof error !== 'object') {
    return String(error);
  }

  // Try to extract from Axios error response (response.data.detail)
  if ('response' in error && error.response && typeof error.response === 'object') {
    const response = error.response as Record<string, unknown>;
    if ('data' in response && response.data && typeof response.data === 'object') {
      const data = response.data as Record<string, unknown>;
      if ('detail' in data && data.detail) {
        return String(data.detail);
      }
    }
  }

  // Try to extract from Error.message
  if ('message' in error && error.message) {
    return String(error.message);
  }

  // Fallback
  return 'An unknown error occurred';
}

/**
 * Type guard to check if an error has a response property (Axios error)
 */
export function isAxiosError(error: unknown): error is { response: { data: { detail: string } } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response: unknown }).response === 'object'
  );
}
